/**
 * Subscription Success Component
 * Handles redirect after successful Stripe checkout
 * Refreshes subscription status and redirects to main app
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Sparkles, CheckCircle } from 'lucide-react';
import './SubscriptionSuccess.css';

function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useSubscription();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    const verifyAndRedirect = async () => {
      try {
        setStatus('verifying');

        // Give Stripe webhook a moment to process
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Refresh subscription status
        await refresh();

        setStatus('success');

        // Wait a moment to show success, then redirect
        await new Promise(resolve => setTimeout(resolve, 1500));

        navigate('/app', { replace: true });
      } catch (err) {
        console.error('Error verifying subscription:', err);
        setError(err.message);
        setStatus('error');

        // Still redirect after error - the subscription gate will handle it
        setTimeout(() => {
          navigate('/app', { replace: true });
        }, 3000);
      }
    };

    verifyAndRedirect();
  }, [searchParams, refresh, navigate]);

  return (
    <div className="subscription-success">
      <div className="success-content">
        {status === 'processing' || status === 'verifying' ? (
          <>
            <Sparkles className="success-icon spinning" size={64} />
            <h1>Processing Your Subscription</h1>
            <p>Please wait while we activate your account...</p>
          </>
        ) : status === 'success' ? (
          <>
            <CheckCircle className="success-icon success" size={64} />
            <h1>Welcome to LumiSkating!</h1>
            <p>Your subscription is now active. Redirecting to your dashboard...</p>
          </>
        ) : (
          <>
            <Sparkles className="success-icon" size={64} />
            <h1>Almost There!</h1>
            <p>Redirecting you to the app...</p>
            {error && <p className="error-text">Note: {error}</p>}
          </>
        )}
      </div>
    </div>
  );
}

export default SubscriptionSuccess;
